import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import type { FeatureCollection } from 'geojson';

const SRC   = 'aviplan-jet-blast-src';
const FILL  = 'aviplan-jb-fill';
const LINE  = 'aviplan-jb-line';
const LABEL = 'aviplan-jb-label';

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

// Color per phase
const PHASE_COLOR: mapboxgl.Expression = [
  'match', ['get', 'phase'],
  'idle',       '#FFA726',
  'taxi',       '#FFD54F',
  'breakaway',  '#FF7043',
  'takeoff',    '#EF5350',
  /* default */ '#FF7043',
];

const JetBlastLayer: React.FC<{
  map: mapboxgl.Map;
  data: FeatureCollection | null;
  visible: boolean;
}> = ({ map, data, visible }) => {

  useEffect(() => {
    if (map.getSource(SRC)) return;

    map.addSource(SRC, { type: 'geojson', data: EMPTY });

    map.addLayer({
      id: FILL, type: 'fill', source: SRC,
      paint: { 'fill-color': PHASE_COLOR, 'fill-opacity': 0.22 },
    });
    map.addLayer({
      id: LINE, type: 'line', source: SRC,
      paint: { 'line-color': PHASE_COLOR, 'line-width': 1.5 },
    });
    map.addLayer({
      id: LABEL, type: 'symbol', source: SRC,
      layout: {
        'text-field': ['concat',
          ['get', 'aircraft_type'], ' · ', ['get', 'phase'],
          ' (', ['to-string', ['get', 'length_m']], ' m)',
        ],
        'text-size': 10,
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
      } as any,
      paint: {
        'text-color': PHASE_COLOR,
        'text-halo-color': '#0d0d14',
        'text-halo-width': 1.2,
      },
    });
  }, [map]);

  useEffect(() => {
    const src = map.getSource(SRC) as mapboxgl.GeoJSONSource | undefined;
    src?.setData(data ?? EMPTY);
  }, [data, map]);

  useEffect(() => {
    const vis = visible ? 'visible' : 'none';
    for (const id of [FILL, LINE, LABEL]) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis);
    }
  }, [visible, map]);

  return null;
};

export default JetBlastLayer;
